export interface FixedStepAdvance {
  readonly steps: number;
  readonly remainder: number;
  readonly dropped: number;
}

/** Unit-agnostic fixed-step accumulator with a max-steps clamp. */
export class FixedStepAccumulator {
  private remainderValue = 0;
  readonly step: number;
  readonly maximumSteps: number;

  constructor(step: number, maximumSteps: number) {
    if (!(step > 0) || !Number.isInteger(maximumSteps) || maximumSteps < 1) {
      throw new Error('FixedStepAccumulator requires a positive step and maximum step count.');
    }
    this.step = step;
    this.maximumSteps = maximumSteps;
  }

  reset(): void {
    this.remainderValue = 0;
  }

  advance(elapsed: number): FixedStepAdvance {
    this.remainderValue += Math.max(0, elapsed);
    let steps = 0;
    while (this.remainderValue + Number.EPSILON >= this.step && steps < this.maximumSteps) {
      this.remainderValue -= this.step;
      steps += 1;
    }
    let dropped = 0;
    if (steps === this.maximumSteps && this.remainderValue >= this.step) {
      dropped = this.remainderValue - (this.remainderValue % this.step);
      this.remainderValue %= this.step;
    }
    return { steps, remainder: this.remainderValue, dropped };
  }
}

export interface BoomClockFrame {
  readonly steps: number;
  /** Fraction [0,1) of a sim step elapsed past the last step — render interpolation alpha. */
  readonly alpha: number;
  readonly droppedSeconds: number;
}

/**
 * Speed-aware wall-clock adapter: feeds `realSeconds * speed` into the
 * accumulator, so pause (speed 0) and fast-forward fall out for free.
 */
export class BoomClock {
  private readonly accumulator: FixedStepAccumulator;
  private previousMs: number | null = null;
  readonly stepSeconds: number;

  constructor(stepSeconds: number, maximumSteps: number) {
    this.accumulator = new FixedStepAccumulator(stepSeconds, maximumSteps);
    this.stepSeconds = stepSeconds;
  }

  reset(nowMs?: number): void {
    this.accumulator.reset();
    this.previousMs = nowMs ?? null;
  }

  advance(nowMs: number, speed: number, update: () => void): BoomClockFrame {
    if (this.previousMs === null) {
      this.previousMs = nowMs;
      return { steps: 0, alpha: 0, droppedSeconds: 0 };
    }
    const realElapsed = Math.max(0, Math.min((nowMs - this.previousMs) / 1_000, 0.25));
    this.previousMs = nowMs;
    const advanced = this.accumulator.advance(realElapsed * speed);
    for (let index = 0; index < advanced.steps; index += 1) update();
    return {
      steps: advanced.steps,
      alpha: advanced.remainder / this.stepSeconds,
      droppedSeconds: advanced.dropped,
    };
  }
}
